import {
  CheckCircleOutlined,
  FileOutlined,
  LockOutlined,
  PlusOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import {
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  message,
} from 'antd';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { API } from '../api';
import { EditableText } from '../components/common/editable-text';
import { PageTitle } from '../components/common/page-title';
import {
  LS_LAYOUT_VALUE_LEY_PLAYLIST_LIST_PAGE,
  LS_LAYOUT_VALUE_LEY_PLAYLIST_PAGE,
  TitleWithLayout,
} from '../components/common/title-with-layout';
import { ContentWrapper } from '../components/layout/upload-video-layout/upload-video-layout.styled';
import { PreviewWrapper } from '../components/uploaded-videos/uploaded-items/components';
import { VideoColItem } from '../components/uploaded-videos/uploaded-items/video-col-item';
import { VideoItem } from '../components/uploaded-videos/uploaded-items/video-item';
import { useHandleLoadPlaylist } from '../hooks/playlists';
import { useLocalStorageValue } from '../hooks/use-local-storage-value';
import { usePlayListPathId } from '../hooks/use-playlist-path-id';
import { LocaleKeys } from '../locale';
import { useAppDispatch } from '../state';
import { useAuthSelector } from '../state/auth/selectors';
import { useMeSelector } from '../state/me/selectors';
import { playlistCurrentActions } from '../state/playlists/currentSlice';
import { playListAllActions } from '../state/playlists/listSlice';
import { usePlaylistCurrentSelector } from '../state/playlists/selectors';
import { Paths } from '../types/common';
import {
  TCourseStructureNode,
  TListedPlaylist,
  TPlayListEditForm,
  TPlaylistContentItem,
} from '../types/responses/video';
import { EVideoQueryMods, EVideoQueryPaths } from '../types/video';
import {
  getPlayCourseItemPath,
  getPlayerLink,
  getStudentPlaylistItemPath,
} from '../utils/paths';
import { isVideoPlaylistItem } from '../utils/playlist-item-kind';
import {
  appendPlaylistToCourseHierarchy,
  buildCourseStructureDraft,
} from '../utils/course-structure';
import { getAiManagerModule1DisplayTitle } from './aimanager-module1-display-titles';

const ButtonsWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: 60px;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const Row = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CourseBuilderPanel = styled('section')`
  margin-bottom: 28px;
  padding: 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._400};
`;

const CourseBuilderGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 12px;
  width: 100%;

  @media (max-width: 840px) {
    grid-template-columns: 1fr;
  }
`;

const CourseBuilderField = styled('label')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 13px;
`;

const CourseBuilderActions = styled('div')`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const CREATE_NEW_VALUE = '__create_new__';

const CourseBuilderText = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.COLORS.WHITE._300};
`;

const CourseBuilderMeta = styled('span')`
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 13px;
`;

const AddButtonContent = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 32px;
`;

const AddButtonWrapper = styled('div')<{ layout: string }>`
  display: ${({ layout }) => (layout.includes('grid') ? 'block' : 'grid')};
  grid-template-columns: ${({ layout }) =>
    layout.includes('grid') ? 'unset' : '3fr 5fr 4fr'};
  position: relative;
`;

const AddButton = styled('div')<{ layout: string }>`
  border-radius: 20px;
  padding-top: ${({ layout }) => (layout.includes('grid') ? '60%' : '0')};
  position: ${({ layout }) =>
    layout.includes('grid') ? 'relative' : 'absolute'};
  top: ${({ layout }) => (layout.includes('grid') ? 'unset' : '0')};
  bottom: ${({ layout }) => (layout.includes('grid') ? 'unset' : '0')};
  left: ${({ layout }) => (layout.includes('grid') ? 'unset' : '0')};
  right: ${({ layout }) => (layout.includes('grid') ? 'unset' : '0')};
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  height: ${({ layout }) => (layout.includes('grid') ? '0' : '100%')};
  border: 2px dashed ${({ theme }) => theme.COLORS.BLACK._500};
  cursor: pointer;
  transition:
    border-color 0.3s ease-out,
    color 0.3s ease-out;

  &:hover {
    border-color: ${({ theme }) => theme.COLORS.ACCENT._200};
    color: ${({ theme }) => theme.COLORS.ACCENT._200};
  }
`;

const AssignedItem = styled('a')<{ completed?: boolean; locked?: boolean }>`
  min-height: 170px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 18px;
  color: ${({ theme }) => theme.COLORS.WHITE._300};
  background: ${({ completed, locked, theme }) =>
    completed
      ? theme.COLORS.BLACK._300
      : locked
        ? theme.COLORS.BLACK._500
        : theme.COLORS.BLACK._400};
  border: 1px solid
    ${({ completed, locked, theme }) =>
      completed
        ? theme.COLORS.ACCENT._200
        : locked
          ? theme.COLORS.BLACK._600
          : theme.COLORS.BLACK._500};
  border-radius: 8px;
  cursor: ${({ locked }) => (locked ? 'not-allowed' : 'pointer')};
  opacity: ${({ locked }) => (locked ? 0.58 : 1)};

  &:hover {
    color: ${({ theme }) => theme.COLORS.ACCENT._200};
    border-color: ${({ theme }) => theme.COLORS.ACCENT._200};
  }
`;

const AssignedIcon = styled('span')`
  font-size: 36px;
  color: ${({ theme }) => theme.COLORS.ACCENT._200};
`;

const AssignedName = styled('strong')`
  font-size: 18px;
  overflow-wrap: anywhere;
`;

const AssignedMeta = styled('span')`
  color: ${({ theme }) => theme.COLORS.BLACK._700};
  font-size: 13px;
`;

const getPlaylistItemAvailabilityText = (item: {
  completed?: boolean;
  locked?: boolean;
}) => {
  if (item.completed) {
    return 'Completed · Available';
  }

  if (item.locked) {
    return 'Locked until previous material is completed';
  }

  return 'Available';
};

const getPlaylistItemDisplayName = (item: TPlaylistContentItem) =>
  getAiManagerModule1DisplayTitle(item.s3Key, item.name);

const getNodeOptions = (nodes: TCourseStructureNode[] | undefined) =>
  (nodes ?? []).map((node) => ({
    label: node.title,
    value: node.id,
  }));

const getSelectedNode = (
  nodes: TCourseStructureNode[] | undefined,
  nodeId?: string
) => (nodes ?? []).find((node) => node.id === nodeId);

const getNodesByType = (
  nodes: TCourseStructureNode[] | undefined,
  type: TCourseStructureNode['type']
) => (nodes ?? []).filter((node) => node.type === type);

export const PlaylistPage: FC = () => {
  const { t } = useTranslation([LocaleKeys.VIDEO]);
  const id = usePlayListPathId();
  const dispatch = useAppDispatch();
  const pendingRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isCourseContext = location.pathname.startsWith(Paths.PLAYCOURSE);
  const { data: authData } = useAuthSelector();
  const { meData } = useMeSelector();

  const { data, pending, error, loaded } = usePlaylistCurrentSelector();

  const [titleButtonLoading, setTitleButtonLoading] = useState(false);
  const [titleEditMode, setTitleEditMode] = useState<boolean>(false);

  const [deletePending, setDeletePending] = useState(false);
  const [courseStructureLoading, setCourseStructureLoading] = useState(false);
  const [moduleCount, setModuleCount] = useState(6);
  const [courseSwitchLoading, setCourseSwitchLoading] = useState(false);
  const [flatPlaylists, setFlatPlaylists] = useState<TListedPlaylist[]>([]);
  const [flatPlaylistsLoading, setFlatPlaylistsLoading] = useState(false);
  const [selectedSourcePlaylistId, setSelectedSourcePlaylistId] =
    useState<string>();
  const [selectedModuleId, setSelectedModuleId] = useState(CREATE_NEW_VALUE);
  const [selectedTopicId, setSelectedTopicId] = useState(CREATE_NEW_VALUE);
  const [selectedLessonId, setSelectedLessonId] = useState(CREATE_NEW_VALUE);
  const [selectedSubtopicId, setSelectedSubtopicId] = useState<string>();
  const [newModuleTitle, setNewModuleTitle] = useState('Module 1');
  const [newTopicTitle, setNewTopicTitle] = useState('Topic 1.1');
  const [newLessonTitle, setNewLessonTitle] = useState('Lesson 1');
  const [newSubtopicTitle, setNewSubtopicTitle] = useState('');
  const isStudentSession = Boolean(authData?.account.studentMode);
  const isOwner = Boolean(
    !isStudentSession && data?.owner?.id && data.owner.id === meData?.id
  );

  const [layout, setLayout] = useLocalStorageValue<
    'colplaylist' | 'gridplaylist'
  >('gridplaylist', LS_LAYOUT_VALUE_LEY_PLAYLIST_LIST_PAGE);

  const handleLoadPlaylist = useHandleLoadPlaylist();

  useEffect(() => {
    if (
      (!loaded && !pending && !error && !pendingRef.current && id) ||
      (!pendingRef.current && !pending && loaded && data?.id !== id && id)
    ) {
      pendingRef.current = true;
      void handleLoadPlaylist(id);
    }
  }, [loaded, pending, error, id, data]);

  useEffect(() => {
    if (!isOwner || !data?.isCourse) return;

    let isActive = true;
    setFlatPlaylistsLoading(true);
    API.playlists
      .getAll({ skip: 0, take: 200 })
      .then((response) => {
        if (!isActive) return;
        setFlatPlaylists(
          response.data.filter(
            (playlist) => !playlist.isCourse && playlist.id !== data.id
          )
        );
      })
      .catch(() => {
        if (isActive) {
          setFlatPlaylists([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setFlatPlaylistsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [data?.id, data?.isCourse, isOwner]);

  const handleEditPlayList = useCallback(
    async (editData: Partial<TPlayListEditForm>) => {
      const loading = message.loading(t('playlist.changing.loading'), 0);
      setTitleButtonLoading(true);

      if (!data) {
        message.error(t('playlist.loading'));
        setTitleButtonLoading(false);
        return;
      }

      try {
        const response = await API.playlists.edit(
          {
            ...data,
            videoIds: data.videos.map((el) => el.id),
            ownerId: data.owner?.id ?? null,
            ...editData,
          },
          data.id
        );

        loading();
        message.success(t('playlist.changing.success'));
        setTitleButtonLoading(false);
        setTitleEditMode(false);

        dispatch(playlistCurrentActions.editPlayList(response.data));
        dispatch(playListAllActions.resetState());
      } catch (e) {
        loading();
        message.error(t('playlist.changing.error'));
        setTitleButtonLoading(false);
      }
    },
    [data, t]
  );

  const courseModules = data?.courseStructure?.nodes ?? [];
  const selectedModule = useMemo(
    () => getSelectedNode(courseModules, selectedModuleId),
    [courseModules, selectedModuleId]
  );
  const courseTopics = selectedModule?.children ?? [];
  const selectedTopic = useMemo(
    () => getSelectedNode(courseTopics, selectedTopicId),
    [courseTopics, selectedTopicId]
  );
  const courseLessons = getNodesByType(selectedTopic?.children, 'lesson');
  const selectedLesson = useMemo(
    () => getSelectedNode(courseLessons, selectedLessonId),
    [courseLessons, selectedLessonId]
  );
  const courseSubtopics = [
    ...getNodesByType(selectedLesson?.children, 'subtopic'),
    ...getNodesByType(selectedTopic?.children, 'subtopic'),
  ];
  const selectedSourcePlaylist = useMemo(
    () =>
      flatPlaylists.find((playlist) => playlist.id === selectedSourcePlaylistId),
    [flatPlaylists, selectedSourcePlaylistId]
  );

  const handleSaveTitle = useCallback(
    (newTitle: string) => {
      if (newTitle && newTitle.length > 0 && newTitle.trim().length > 0) {
        void handleEditPlayList({
          title: newTitle,
        });
      } else {
        void message.warning(t('playlist.changing.warning'));
      }
    },
    [handleEditPlayList, t]
  );

  const handleToggleCourse = useCallback(
    async (checked: boolean) => {
      setCourseSwitchLoading(true);
      await handleEditPlayList({ isCourse: checked });
      setCourseSwitchLoading(false);
    },
    [handleEditPlayList]
  );

  const handleGenerateCourseStructure = useCallback(async () => {
    if (!data) return;

    setCourseStructureLoading(true);
    await handleEditPlayList({
      courseStructure: buildCourseStructureDraft(data.items ?? [], moduleCount),
      isCourse: true,
    });
    setCourseStructureLoading(false);
  }, [data, handleEditPlayList, moduleCount]);

  const handleUseAutoCourseStructure = useCallback(async () => {
    setCourseStructureLoading(true);
    await handleEditPlayList({ courseStructure: null });
    setCourseStructureLoading(false);
  }, [handleEditPlayList]);

  const handleAppendPlaylistToHierarchy = useCallback(async () => {
    if (!data || !selectedSourcePlaylist) {
      message.warning('Select a flat playlist source first');
      return;
    }

    const sourceItems = selectedSourcePlaylist.items ?? [];
    if (!sourceItems.length) {
      message.warning('Selected playlist has no items');
      return;
    }

    const moduleTitle =
      selectedModuleId === CREATE_NEW_VALUE
        ? newModuleTitle.trim()
        : selectedModule?.title ?? '';
    const topicTitle =
      selectedTopicId === CREATE_NEW_VALUE
        ? newTopicTitle.trim()
        : selectedTopic?.title ?? '';
    const lessonTitle =
      selectedLessonId === CREATE_NEW_VALUE
        ? newLessonTitle.trim() || selectedSourcePlaylist.title
        : selectedLesson?.title ?? '';
    const subtopicTitle =
      selectedSubtopicId === CREATE_NEW_VALUE
        ? newSubtopicTitle.trim()
        : getSelectedNode(courseSubtopics, selectedSubtopicId)?.title;

    if (!moduleTitle || !topicTitle || !lessonTitle) {
      message.warning('Module, topic and lesson titles are required');
      return;
    }

    setCourseStructureLoading(true);
    const result = appendPlaylistToCourseHierarchy({
      courseItems: data.items ?? [],
      courseStructure: data.courseStructure ?? { nodes: [], version: 1 },
      placement: {
        moduleId:
          selectedModuleId === CREATE_NEW_VALUE ? undefined : selectedModuleId,
        moduleTitle,
        lessonId:
          selectedLessonId === CREATE_NEW_VALUE ? undefined : selectedLessonId,
        lessonTitle,
        subtopicId:
          selectedSubtopicId && selectedSubtopicId !== CREATE_NEW_VALUE
            ? selectedSubtopicId
            : undefined,
        subtopicTitle,
        topicId:
          selectedTopicId === CREATE_NEW_VALUE ? undefined : selectedTopicId,
        topicTitle,
      },
      sourcePlaylistId: selectedSourcePlaylist.id,
      sourceItems,
    });

    await handleEditPlayList({
      courseStructure: result.courseStructure,
      isCourse: true,
      items: result.items,
    });
    message.success(
      `Linked ${result.linkedItemCount} items, added ${result.addedItemCount} new flat items`
    );
    setCourseStructureLoading(false);
  }, [
    courseSubtopics,
    data,
    handleEditPlayList,
    message,
    newLessonTitle,
    newModuleTitle,
    newSubtopicTitle,
    newTopicTitle,
    selectedLesson,
    selectedLessonId,
    selectedModule,
    selectedModuleId,
    selectedSourcePlaylist,
    selectedSubtopicId,
    selectedTopic,
    selectedTopicId,
  ]);

  const handleShare = useCallback(() => {
    try {
      navigator.clipboard.writeText(window.location.href).then(() => {
        message.success(t('playlist.copy.success'));
      });
    } catch (e) {
      message.error(t('playlist.copy.error'));
    }
  }, [id, t]);

  const handleAddVideo = useCallback(() => {
    navigate(
      `${Paths.UPLOADED_VIDEOS}?${EVideoQueryPaths.PLAYLIST_ID}=${
        id ?? 'not-found'
      }&${EVideoQueryPaths.MODE}=${EVideoQueryMods.ADD_VIDEO}`
    );
  }, [id]);

  const handleDeletePlayList = useCallback(async () => {
    if (id) {
      const loading = message.loading(t('playlist.deleting.loading'), 0);
      setDeletePending(true);

      try {
        await API.playlists.delete(id);

        loading();
        dispatch(playListAllActions.resetState());
        message.success(t('playlist.deleting.success'));
        setDeletePending(false);
        navigate(
          data?.isCourse ? Paths.UPLOADED_PLAYCOURSES : Paths.UPLOADED_VIDEOS_PLAYLIST
        );
        dispatch(playlistCurrentActions.resetState());
      } catch (e) {
        loading();
        message.error(t('playlist.deleting.error'));
        setDeletePending(false);
      }
    }
  }, [id, t]);

  return (
    <div>
      <TitleWithLayout layout={layout} onChange={setLayout} />
      <ButtonsWrapper>
        <Row>
          <PageTitle> {t('playlist.title')}</PageTitle>
          {isOwner ? (
            <>
              <EditableText
                initialValue={data?.title ?? ''}
                onSaveNew={handleSaveTitle}
                okButtonProps={{
                  loading: titleButtonLoading,
                }}
                editMode={titleEditMode}
                setEditMode={setTitleEditMode}
              />
              <Switch
                checked={Boolean(data?.isCourse)}
                checkedChildren="Course"
                unCheckedChildren="Course"
                loading={courseSwitchLoading}
                onChange={(checked) => void handleToggleCourse(checked)}
              />
            </>
          ) : (
            <PageTitle>{data?.title ?? ''}</PageTitle>
          )}
        </Row>
        {/* <Button type="primary" onClick={handleShare}>
          {t('playlist.copy.share')}
        </Button> */}
        {isOwner && (
          <Row>
            <Button onClick={handleAddVideo}>{t('playlist.change')}</Button>
            <Popconfirm
              title={t('playlist.deleting.title')}
              description={t('playlist.deleting.description')}
              onConfirm={handleDeletePlayList}
              okText={t('buttons.deleting.success')}
              cancelText={t('buttons.cancel')}
              okButtonProps={{
                danger: true,
                loading: deletePending,
              }}
              cancelButtonProps={{
                type: 'primary',
                loading: deletePending,
              }}
              placement="right"
            >
              <Button danger loading={deletePending}>
                {t('playlist.delete')}
              </Button>
            </Popconfirm>
          </Row>
        )}
      </ButtonsWrapper>
      {isOwner && data?.isCourse && (
        <CourseBuilderPanel>
          <CourseBuilderText>
            <strong>Play course structure</strong>
            <CourseBuilderMeta>
              Flat playlist compatibility is preserved: {data.items?.length ?? 0}{' '}
              items remain in one ordered list.
              {' · '}
              Current modules:{' '}
              {data.courseStructure?.nodes?.length ?? 'auto from files'}
            </CourseBuilderMeta>
          </CourseBuilderText>
          <Row>
            <span>Modules</span>
            <InputNumber
              min={1}
              max={24}
              value={moduleCount}
              onChange={(value) => setModuleCount(Number(value) || 1)}
            />
            <Button
              type="primary"
              loading={courseStructureLoading}
              onClick={() => void handleGenerateCourseStructure()}
            >
              Generate structure
            </Button>
            <Button
              loading={courseStructureLoading}
              onClick={() => void handleUseAutoCourseStructure()}
            >
              Auto
            </Button>
          </Row>
          <CourseBuilderGrid>
            <CourseBuilderField>
              Flat playlist source
              <Select
                loading={flatPlaylistsLoading}
                placeholder="Select playlist"
                value={selectedSourcePlaylistId}
                options={flatPlaylists.map((playlist) => ({
                  label: `${playlist.title} (${playlist.items?.length ?? 0})`,
                  value: playlist.id,
                }))}
                onChange={setSelectedSourcePlaylistId}
              />
            </CourseBuilderField>
            <CourseBuilderField>
              Module
              <Select
                value={selectedModuleId}
                options={[
                  { label: 'Create new module', value: CREATE_NEW_VALUE },
                  ...getNodeOptions(courseModules),
                ]}
                onChange={(value) => {
                  setSelectedModuleId(value);
                  setSelectedTopicId(CREATE_NEW_VALUE);
                  setSelectedLessonId(CREATE_NEW_VALUE);
                  setSelectedSubtopicId(undefined);
                }}
              />
              {selectedModuleId === CREATE_NEW_VALUE && (
                <Input
                  value={newModuleTitle}
                  placeholder="Module title"
                  onChange={(event) => setNewModuleTitle(event.target.value)}
                />
              )}
            </CourseBuilderField>
            <CourseBuilderField>
              Topic
              <Select
                value={selectedTopicId}
                options={[
                  { label: 'Create new topic', value: CREATE_NEW_VALUE },
                  ...getNodeOptions(courseTopics),
                ]}
                onChange={(value) => {
                  setSelectedTopicId(value);
                  setSelectedLessonId(CREATE_NEW_VALUE);
                  setSelectedSubtopicId(undefined);
                }}
              />
              {selectedTopicId === CREATE_NEW_VALUE && (
                <Input
                  value={newTopicTitle}
                  placeholder="Topic title"
                  onChange={(event) => setNewTopicTitle(event.target.value)}
                />
              )}
            </CourseBuilderField>
            <CourseBuilderField>
              Lesson
              <Select
                value={selectedLessonId}
                options={[
                  { label: 'Create new lesson', value: CREATE_NEW_VALUE },
                  ...getNodeOptions(courseLessons),
                ]}
                onChange={(value) => {
                  setSelectedLessonId(value);
                  setSelectedSubtopicId(undefined);
                }}
              />
              {selectedLessonId === CREATE_NEW_VALUE && (
                <Input
                  value={newLessonTitle}
                  placeholder="Lesson title"
                  onChange={(event) => setNewLessonTitle(event.target.value)}
                />
              )}
            </CourseBuilderField>
            <CourseBuilderField>
              Subtopic (optional)
              <Select
                allowClear
                placeholder="No subtopic"
                value={selectedSubtopicId}
                options={[
                  { label: 'Create new subtopic', value: CREATE_NEW_VALUE },
                  ...getNodeOptions(courseSubtopics),
                ]}
                onChange={setSelectedSubtopicId}
              />
              {selectedSubtopicId === CREATE_NEW_VALUE && (
                <Input
                  value={newSubtopicTitle}
                  placeholder="Subtopic title"
                  onChange={(event) => setNewSubtopicTitle(event.target.value)}
                />
              )}
            </CourseBuilderField>
          </CourseBuilderGrid>
          <CourseBuilderActions>
            <Button
              type="primary"
              loading={courseStructureLoading}
              disabled={!selectedSourcePlaylistId}
              onClick={() => void handleAppendPlaylistToHierarchy()}
            >
              Add playlist to hierarchy
            </Button>
            {selectedSourcePlaylist && (
              <CourseBuilderMeta>
                {selectedSourcePlaylist.items?.length ?? 0} flat items selected
              </CourseBuilderMeta>
            )}
          </CourseBuilderActions>
        </CourseBuilderPanel>
      )}
      <ContentWrapper layout={layout}>
        {data?.items?.length
          ? data.items.map((item, index) => {
              const href = isOwner
                ? isVideoPlaylistItem(item)
                  ? getPlayerLink(item.linkId)
                  : `${Paths.MODULE1_VIEW}?assetLinkId=${encodeURIComponent(
                      item.linkId
                    )}&from=${encodeURIComponent(Paths.PLAYLIST)}`
                : isCourseContext
                  ? getPlayCourseItemPath(data.id, index)
                  : getStudentPlaylistItemPath(data.id, index);
              return (
                <AssignedItem
                  key={item.linkId}
                  href={item.locked ? undefined : href}
                  completed={item.completed}
                  locked={item.locked}
                  title={getPlaylistItemAvailabilityText(item)}
                  onClick={(event) => {
                    if (item.locked) event.preventDefault();
                  }}
                >
                  <AssignedIcon>
                    {item.locked ? (
                      <LockOutlined />
                    ) : item.completed ? (
                      <CheckCircleOutlined />
                    ) : isVideoPlaylistItem(item) ? (
                      <VideoCameraOutlined />
                    ) : (
                      <FileOutlined />
                    )}
                  </AssignedIcon>
                  <AssignedName>
                    {item.order ?? index + 1}.{' '}
                    {getPlaylistItemDisplayName(item)}
                  </AssignedName>
                  <AssignedMeta>
                    {item.kind.toUpperCase()} · Education
                    {' · '}
                    {getPlaylistItemAvailabilityText(item)}
                  </AssignedMeta>
                </AssignedItem>
              );
            })
          : isOwner
            ? data?.videos.map((el) =>
                layout.includes('grid') ? (
                  <VideoItem key={el.id} videoData={el} />
                ) : (
                  <VideoColItem key={el.id} videoData={el} />
                )
              )
            : null}
        {isOwner && (
          <AddButtonWrapper layout={layout}>
            {layout.includes('col') && <PreviewWrapper />}
            <AddButton onClick={handleAddVideo} layout={layout}>
              <AddButtonContent>
                <PlusOutlined />
              </AddButtonContent>
            </AddButton>
          </AddButtonWrapper>
        )}
      </ContentWrapper>
    </div>
  );
};
